import { Injectable } from '@angular/core';
import { QuestionEntityModels } from '@medsurf/flat-models';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MatomoControlService {
  #consentDuration = 365; // days
  #consentStorageKey = 'matomo.consentGiven';
  hasConsent$ = new BehaviorSubject<boolean>(false);

  constructor() {
    if (this.#hasConsent()) {
      this.hasConsent$.next(true);
      this.#push(['setConsentGiven']);
    }
  }

  #enabled(): boolean {
    return (typeof (window as any)._paq === 'object');
  }

  #push(args: any[]): void {
    if (this.#enabled()) {
      (window as any)._paq.push(args);
    }
  }

  #hasConsent() {
    const value = localStorage.getItem(this.#consentStorageKey);
    const consentTimestamp = value ? parseInt(value) : 0;
    return consentTimestamp > Date.now() - (this.#consentDuration * 24 * 60 * 60 * 1000);
  }

  giveConsent() {
    localStorage.setItem(this.#consentStorageKey, `${Date.now()}`);
    this.#push(['setConsentGiven']);
    this.hasConsent$.next(true);
  }

  trackPageView(url: string, title: string): void {
    this.#push(['setCustomUrl', url]);
    this.#push(['setDocumentTitle', title]);
    this.#push(['trackPageView']);
  }

  trackSearch(query: string, count: number | boolean = false): void {
    this.#push(['trackSiteSearch', query, false, count]);
  }

  trackEvent(category: string, action: string, name: string, value: number): void {
    const a = ['trackEvent', category, action, name, value];
    this.#push(a);
  }

  trackQuizStart(slideIds: string[] | number[]): void {
    const sorted = [...slideIds].sort();
    this.trackEvent('Quiz', 'started', sorted.join(','), sorted.length);
  }

  trackQuizEnd(slideIds: string[] | number[], correctPercentage: number): void {
    const sorted = [...slideIds].sort();
    this.trackEvent('Quiz', 'finished', sorted.join(','), correctPercentage);
  }

  trackQuizAnswer(slideId: string, question: QuestionEntityModels.Question, correct: boolean, key?: string) {
    const el = document.createElement('div');
    el.innerHTML = question.content as string;
    const questionText = el.textContent;
    let name = `${slideId} > ${questionText}`;
    if (key) {
      name += ` > ${key}`;
    }
    this.trackEvent('Question', 'answered ' + (correct ? 'correct' : 'wrong'), name, correct ? 1 : 0);
  }

  trackQuizSlide(slideId: string, correctPercentage: number) {
    this.trackEvent('Quiz Slide', 'answered', slideId, correctPercentage);
  }
}